// src/features/medicalSercices/page/ExceptionForm.tsx

import {
    Button,
    Card,
    Col,
    Form,
    Input,
    Row,
    Select,
    TreeSelect,
    message,
    Radio,
} from "antd";
import { post, get } from "@/common/services/httpClient";
import { useForm } from "antd/es/form/Form";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useAppMutation } from "@/common/hooks/useAppMutation";
import { useTranslation } from "react-i18next";
import { API_ENDPOINTS, QUERY_KEYS } from "../../../../participants/api/apiEndpoints";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UploadFile } from "antd/es/upload/interface";
import AgreementModal from "./AgreementModal";

// --- Type Definitions for API Responses ---
interface DropdownItem {
    Id: number;
    Name: string;
    NameAr?: string;
    ISOCode: string | null;
}

interface ExceptionItem {
    id?: number; // optional for new exceptions
    name: string;
}

interface ExceptionFormValues {
    companyId: number;
    companyName: string;
    serviceCategoryItemId?: number;
    serviceCategoryItemName?: string;
    exceptions: ExceptionItem[];
    isGeneralException: boolean; // new field
    agreementFile?: UploadFile[];
}

const useDropdownData = (dropdownName: string) => {
    return useQuery({
        queryKey: ['dropdownData', dropdownName],
        queryFn: async (): Promise<{ label: string; value: number; labelAr?: string; ISOCode: string | null }[]> => {
            const response: Record<string, DropdownItem[]> = await post('/Lookup/GetDropdownData', { dropdowns: [dropdownName] });
            return response[dropdownName].map((item: DropdownItem) => ({ label: item.Name, value: item.Id, ISOCode: item.ISOCode }));
        },
        staleTime: Infinity, // Data is unlikely to change often
    });
};

const useServicesByCompany = (companyId?: number) => {
    return useQuery({
        queryKey: ['servicesByCompany', companyId],
        queryFn: async (): Promise<any[]> => {
            if (!companyId) return [];
            const response = await get(`/Company/GetServices?companyId=${companyId}`);
            return response?.data || [];
        },
        staleTime: Infinity,
        enabled: !!companyId,
    });
};

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const ExceptionForm = () => {
    const [form] = useForm<ExceptionFormValues>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { state } = useRouter().state.location;
    const record = (state as { record?: any })?.record;
    const id = record?.id;
    const isEdit = !!id;

    const { data: companies } = useDropdownData('company');
    const companyId = Form.useWatch("companyId", form);
    const { data: servicesRaw = [], isLoading: loadingServices } = useServicesByCompany(companyId);


    const [existingAgreement, setExistingAgreement] = useState<UploadFile[]>([]);
    const [agreementFile, setAgreementFile] = useState<UploadFile[]>([]);
    const [agreementModalOpen, setAgreementModalOpen] = useState(false);

    // Build tree data for services with parent categories
    const buildServiceTreeData = () => {
        // API already returns grouped data with categories and services
        return (servicesRaw || [])
            .map((cat: any) => ({
                title: cat.serviceCategoryName,
                key: `cat-${cat.serviceCategoryId}`,
                value: `cat-${cat.serviceCategoryId}`,
                selectable: false,
                children: (cat.services || []).map((s: any) => ({
                    title: s.serviceCategoryItemName,
                    key: String(s.serviceCategoryItemId),
                    value: String(s.serviceCategoryItemId),
                    isLeaf: true,
                })),
            }))
            .filter((c: any) => (c.children || []).length > 0);
    };

    const serviceTreeData = buildServiceTreeData();


    // const [loadingExceptions, setLoadingExceptions] = useState(false);
    // Fetch existing exceptions whenever company or service changes
    const fetchExceptions = useCallback(async (
        companyId?: number,
        serviceId?: number,
        isGeneralException?: boolean
    ) => {
        if (!companyId) {
            form.setFieldsValue({ exceptions: [{ name: "", id: undefined }] });
            setExistingAgreement([]);
            return;
        }

        // setLoadingExceptions(true);
        try {
            const response = await post<any>("/CompanyException/GetByCompanyService", {
                companyId,
                serviceCategoryItemId: serviceId,
                isGeneralException: isGeneralException
            });

            const { exceptions = [], agreementFilePath } = response?.data ?? {};


            // Map exceptions for Form.List
            const data: ExceptionItem[] = exceptions.length
                ? exceptions.map((e: any) => ({ id: e.id, name: e.name }))
                : [{ name: "", id: undefined }]; // placeholder row


            form.setFieldsValue({
                exceptions: data,
            });

            // Set existing agreement file
            if (agreementFilePath) {

                const fileObj: UploadFile = {
                    uid: "-1",
                    name: agreementFilePath.split("/").pop() || "agreement",
                    status: 'done' as const, // fix TS error
                    //url: agreementFilePath,
                    url: `${VITE_API_BASE_URL.replace(/api$/, '')}${agreementFilePath}`,
                };

                setExistingAgreement([fileObj]);
                setAgreementFile([fileObj]); 
                form.setFieldsValue({ agreementFile: [fileObj] }); // Form value bhi update
            } else {
                setExistingAgreement([]);
                setAgreementFile([]);
                form.setFieldsValue({ agreementFile: [] });
            }

        } catch (error) {
            console.error("Fetch exceptions error:", error);
            message.error(t("Failed to load exceptions"));
            form.setFieldsValue({ exceptions: [{ name: "", id: undefined }] });
            setExistingAgreement([]);
        } finally {
            // setLoadingExceptions(false);
        }
    }, [form, t]);

    // Watch companyId & serviceCategoryItemId
    const serviceCategoryItemId = Form.useWatch("serviceCategoryItemId", form);
    const isGeneralException = Form.useWatch("isGeneralException", form);
    const selectedCompany = companies?.find(
        c => c.value === companyId
    );

    useEffect(() => {
        fetchExceptions(companyId, serviceCategoryItemId, isGeneralException);
    }, [companyId, serviceCategoryItemId, isGeneralException, fetchExceptions]);

    // Handle company change - clear service and exceptions
    const handleCompanyChange = (newCompanyId: number) => {
        // Clear service and exceptions
        form.setFieldsValue({
            serviceCategoryItemId: undefined,
            exceptions: [{ name: "", id: undefined }],
            companyId: newCompanyId,
        });
    };


    /* ---------------- Submit ---------------- */

    const mutation = useAppMutation(
        (values: ExceptionFormValues) =>
            post(isEdit ? API_ENDPOINTS.EXCEPTIONS.UPDATE : API_ENDPOINTS.EXCEPTIONS.SAVE, {
                ...values,
                id,
            }),
        {
            onSuccess: (response: any) => {

                const result = response;
                //console.log(response);

                if (!result) {
                    message.error(t("Unexpected response from server"));
                    return;
                }

                if (result.isError === true) {
                    message.warning(t(result.message));
                } else {
                    message.success(
                        t(isEdit ? "Exception updated successfully" : "Exception saved successfully")
                    );
                }

                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.EXCEPTIONS.EXCEPTIONS],
                });

                navigate({ to: ".." });
            },
            onError: (error: any) => {
                message.error(error?.message || t("Something went wrong"));
            },
        }
    );

    const onFinish = async (values: ExceptionFormValues & { agreementFile?: any }) => {

        const selectedCompanyData = companies?.find(c => c.value === values.companyId);
        
        let selectedServiceData: any = null;
        if (!values.isGeneralException && values.serviceCategoryItemId) {
            // Find the service from the grouped structure
            for (const category of servicesRaw) {
                const service = (category.services || []).find(
                    (s: any) => String(s.serviceCategoryItemId) === String(values.serviceCategoryItemId)
                );
                if (service) {
                    selectedServiceData = service;
                    break;
                }
            }
        }

        if (!selectedCompanyData) {
            message.error(t("Invalid company selection"));
            return;
        }

        if (!values.isGeneralException && !selectedServiceData) {
            message.error(t("Invalid service selection"));
            return;
        }

        mutation.mutate({
            ...values,
            agreementFile: agreementFile, 
            companyName: selectedCompanyData.label,
            serviceCategoryItemName: values.isGeneralException ? undefined : selectedServiceData?.serviceCategoryItemName,
        });
    };


    /* ---------------- Render ---------------- */

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button htmlType="button" onClick={() => navigate({ to: '..' })}>
                    {t("Go Back")}
                </Button>
            </div>

            <Card title={t("Company Exception Form")}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        exceptions: [{ name: "" }],
                    }}
                >
                    <Row gutter={48} >
                        <Col span={8}>
                            <Form.Item
                                name="companyId"
                                label={t("Company")}
                                rules={[{ required: true, message: t("Please select a company") }]}
                            >
                                <Select
                                    placeholder={t("Select company")}
                                    options={companies}
                                    disabled={isEdit}
                                    showSearch={true}
                                    optionFilterProp="label"
                                    onChange={handleCompanyChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item name="isGeneralException" label={t("Exception Type")} initialValue={false}>
                                <Radio.Group>
                                    <Radio value={false}>{t("Service")}</Radio>
                                    <Radio value={true}>{t("General")}</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>

                        <Col span={11}>
                            {!Form.useWatch("isGeneralException", form) && (
                                <Form.Item
                                    name="serviceCategoryItemId"
                                    label={t("Service")}
                                    rules={[
                                        { required: true, message: t("Select category service item") },
                                    ]}
                                >
                                    <TreeSelect
                                        treeData={serviceTreeData}
                                        loading={loadingServices}
                                        placeholder={t("Select category service item")}
                                        treeDefaultExpandAll
                                        allowClear
                                        showSearch
                                        disabled={!companyId}
                                        treeNodeFilterProp="title"
                                        treeNodeLabelProp="title"
                                    />
                                </Form.Item>
                            )}
                        </Col>

                        {companyId && (
                            <Col span={24}>
                                <Form.Item label={t("Agreement.CompanyAgreement")}>
                                    <Button
                                        type="dashed"
                                        onClick={() => setAgreementModalOpen(true)}
                                    >
                                        {existingAgreement.length
                                            ? t("Agreement.ViewReplace")
                                            : t("Agreement.Add")}
                                    </Button>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={24}>
                            <Form.List name="exceptions">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <Form.Item
                                                key={field.key}
                                                required={true}
                                            >

                                                {/* 🔐 HIDDEN ID FIELD (THIS IS REQUIRED) */}
                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "id"]}
                                                    hidden
                                                >
                                                    <Input />
                                                </Form.Item>

                                                {/* Exception Name */}

                                                <Form.Item
                                                    {...field}
                                                    name={[field.name, "name"]}
                                                    validateTrigger={["onBlur", "onChange"]}
                                                    validateFirst={true}
                                                    rules={[
                                                        { required: true, message: t("Please enter exception name") },

                                                        ({ getFieldValue }) => ({
                                                            validator(_, value) {
                                                                const list =
                                                                    getFieldValue("exceptions")?.map(
                                                                        (e: ExceptionItem) => e?.name?.trim()
                                                                    ) || [];

                                                                const duplicates = list.filter(
                                                                    (v: string, i: number) =>
                                                                        v === value?.trim() && i !== index
                                                                );

                                                                if (duplicates.length) {
                                                                    return Promise.reject(
                                                                        t("Exception names must be unique")
                                                                    );
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        }),
                                                    ]}
                                                    noStyle
                                                >
                                                    <Input
                                                        placeholder={t("Exception name")}
                                                        style={{ width: "90%", marginRight: 8 }}
                                                    />
                                                </Form.Item>

                                                {fields.length > 1 && (
                                                    <Button
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => remove(field.name)}
                                                    />
                                                )}
                                            </Form.Item>
                                        ))}

                                        <Form.Item>
                                            <Button
                                                type="dashed"
                                                onClick={() => add({ name: "" })}
                                                icon={<PlusOutlined />}
                                                block
                                            >
                                                {t("Add Exception")}
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form.List>
                        </Col>


                    </Row>

                    <Row justify="end">
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <Button onClick={() => form.resetFields()} style={{ marginRight: 8 }}>
                                {t("Reset")}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                            // loading={mutation?.isLoading || loadingExceptions}
                            // disabled={mutation?.isLoading || loadingExceptions}
                            >
                                {t("Save")}
                            </Button>
                        </div>
                    </Row>
                </Form>
            </Card>

            <AgreementModal
                open={agreementModalOpen}
                onClose={() => setAgreementModalOpen(false)}
                companyId={companyId}
                companyName={selectedCompany?.label}
                //existingFile={agreementFile}
                existingFile={agreementFile}
                onFileChange={(files, rawFile) => {
                    if (rawFile) {
                        setAgreementFile([{
                            ...files[0],
                            url: URL.createObjectURL(rawFile)
                        }]);
                    } else {
                        setAgreementFile(files);
                    }
                }}
            />

        </>
    );
};

export default ExceptionForm;
